/**
 * @file api.ts
 * @description Defines a set of functions for interacting with the backend API of the agent dashboard application. It includes methods for fetching statistics, managing sessions and agents, retrieving analytics data, handling settings, and managing model pricing. The module abstracts away the details of making HTTP requests and provides a clean interface for the rest of the application to use when communicating with the server.
 * @author Son Nguyen <hoangson091104@gmail.com>
 */

import type {
  Agent,
  Analytics,
  CostResult,
  DashboardEvent,
  ModelPricing,
  Session,
  SessionDrillIn,
  SessionStats,
  Stats,
  TranscriptListResult,
  TranscriptResult,
  UpdateStatusPayload,
  WorkflowData,
} from "./types";

const BASE = "/api";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error?.message || `HTTP ${res.status}`);
  }
  return res.json();
}

export const api = {
  updates: {
    status: () => request<UpdateStatusPayload>("/updates/status"),
    check: () =>
      request<UpdateStatusPayload>("/updates/check", {
        method: "POST",
        body: JSON.stringify({}),
      }),
  },

  stats: {
    get: () => request<Stats>(`/stats?tz_offset=${new Date().getTimezoneOffset()}`),
  },

  sessions: {
    facets: () => request<{ cwds: string[] }>("/sessions/facets"),
    list: (params?: {
      status?: string;
      q?: string;
      cwd?: string;
      sort_by?: string;
      sort_desc?: boolean;
      limit?: number;
      offset?: number;
    }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.q) qs.set("q", params.q);
      if (params?.cwd) qs.set("cwd", params.cwd);
      if (params?.sort_by) qs.set("sort_by", params.sort_by);
      if (params?.sort_desc !== undefined) qs.set("sort_desc", String(params.sort_desc));
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const queryString = qs.toString();
      return request<{ sessions: Session[]; total: number; limit: number; offset: number }>(
        `/sessions${queryString ? `?${queryString}` : ""}`
      );
    },
    get: (id: string) =>
      request<{ session: Session; agents: Agent[]; events: DashboardEvent[] }>(
        `/sessions/${encodeURIComponent(id)}`
      ),
    stats: (id: string) => request<SessionStats>(`/sessions/${encodeURIComponent(id)}/stats`),
    transcripts: (id: string) =>
      request<TranscriptListResult>(`/sessions/${encodeURIComponent(id)}/transcripts`),
    transcript: (
      id: string,
      params?: {
        agent_id?: string;
        limit?: number;
        offset?: number;
        after?: number;
        before?: number;
      }
    ) => {
      const qs = new URLSearchParams();
      if (params?.agent_id) qs.set("agent_id", params.agent_id);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      if (params?.after != null) qs.set("after", String(params.after));
      if (params?.before != null) qs.set("before", String(params.before));
      const q = qs.toString();
      return request<TranscriptResult>(
        `/sessions/${encodeURIComponent(id)}/transcript${q ? `?${q}` : ""}`
      );
    },
  },

  agents: {
    list: (params?: { status?: string; session_id?: string; limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.session_id) qs.set("session_id", params.session_id);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.offset) qs.set("offset", String(params.offset));
      const q = qs.toString();
      return request<{ agents: Agent[] }>(`/agents${q ? `?${q}` : ""}`);
    },
  },

  events: {
    list: (params?: {
      event_type?: string[];
      tool_name?: string[];
      agent_id?: string[];
      session_id?: string | string[];
      q?: string;
      from?: string;
      to?: string;
      limit?: number;
      offset?: number;
    }) => {
      const qs = new URLSearchParams();
      const csv = (v?: string[]) => (v && v.length > 0 ? v.join(",") : undefined);
      const et = csv(params?.event_type);
      const tn = csv(params?.tool_name);
      const ag = csv(params?.agent_id);
      const sid = Array.isArray(params?.session_id) ? csv(params?.session_id) : params?.session_id;
      if (et) qs.set("event_type", et);
      if (tn) qs.set("tool_name", tn);
      if (ag) qs.set("agent_id", ag);
      if (sid) qs.set("session_id", sid);
      if (params?.q) qs.set("q", params.q);
      if (params?.from) qs.set("from", params.from);
      if (params?.to) qs.set("to", params.to);
      if (params?.limit != null) qs.set("limit", String(params.limit));
      if (params?.offset != null) qs.set("offset", String(params.offset));
      const q = qs.toString();
      return request<{
        events: DashboardEvent[];
        limit: number;
        offset: number;
        total: number;
      }>(`/events${q ? `?${q}` : ""}`);
    },
    facets: () => request<{ event_types: string[]; tool_names: string[] }>("/events/facets"),
  },

  analytics: {
    get: () => request<Analytics>(`/analytics?tz_offset=${new Date().getTimezoneOffset()}`),
  },

  settings: {
    info: () =>
      request<{
        db: {
          path: string;
          size: number;
          counts: Record<string, number>;
          pragmas: {
            journal_mode: string;
            synchronous: number;
            auto_vacuum: number;
            encoding: string;
            foreign_keys: number;
            busy_timeout: number;
          };
          load_stats: { m5: number; m15: number; h1: number };
        };
        hooks: { installed: boolean; path: string; hooks: Record<string, boolean> };
        server: {
          uptime: number;
          node_version: string;
          platform: string;
          ws_connections: number;
          memory: { rss: number; heapTotal: number; heapUsed: number; external: number };
          cpu_load: number[];
          arch: string;
          total_mem: number;
          free_mem: number;
          cpus: number;
        };
        transcript_cache: {
          size: number;
          maxSize: number;
          hits: number;
          misses: number;
          keys: string[];
        };
      }>("/settings/info"),
    claudeHome: {
      get: () => request<{ claude_home: string }>("/settings/claude-home"),
      set: (path: string) =>
        request<{ ok: boolean; claude_home: string }>("/settings/claude-home", {
          method: "PUT",
          body: JSON.stringify({ path }),
        }),
    },
    clearData: () =>
      request<{ ok: boolean; cleared: Record<string, number> }>("/settings/clear-data", {
        method: "POST",
      }),
    reimport: () =>
      request<{ ok: boolean; imported: number; skipped: number; errors: number }>(
        "/settings/reimport",
        { method: "POST" }
      ),
    reinstallHooks: () =>
      request<{ ok: boolean; hooks: { installed: boolean; hooks: Record<string, boolean> } }>(
        "/settings/reinstall-hooks",
        { method: "POST" }
      ),
    resetPricing: () =>
      request<{ ok: boolean; pricing: ModelPricing[] }>("/settings/reset-pricing", {
        method: "POST",
      }),
    exportData: () => `${BASE}/settings/export`,
    cleanup: (params: { abandon_hours?: number; purge_days?: number }) =>
      request<{
        ok: boolean;
        abandoned: number;
        purged_sessions: number;
        purged_events: number;
        purged_agents: number;
      }>("/settings/cleanup", { method: "POST", body: JSON.stringify(params) }),
  },

  workflows: {
    get: (status?: string) =>
      request<WorkflowData>(`/workflows${status && status !== "all" ? `?status=${status}` : ""}`),
    session: (id: string) =>
      request<SessionDrillIn>(`/workflows/session/${encodeURIComponent(id)}`),
  },

  pricing: {
    list: () => request<{ pricing: ModelPricing[] }>("/pricing"),
    upsert: (data: Omit<ModelPricing, "updated_at">) =>
      request<{ pricing: ModelPricing }>("/pricing", {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    delete: (pattern: string) =>
      request<{ ok: boolean }>(`/pricing/${encodeURIComponent(pattern)}`, {
        method: "DELETE",
      }),
    totalCost: () =>
      request<CostResult>(`/pricing/cost?tz_offset=${new Date().getTimezoneOffset()}`),
    sessionCost: (sessionId: string) =>
      request<CostResult>(
        `/pricing/cost/${encodeURIComponent(sessionId)}?tz_offset=${new Date().getTimezoneOffset()}`
      ),
  },

  import: {
    guide: () =>
      request<{
        platform: string;
        default_projects_dir: string;
        default_projects_dir_display: string;
        default_projects_dir_exists: boolean;
        default_projects_dir_stats: { projects: number; jsonl_files: number };
        archive_command: string;
        supported_extensions: string[];
        max_upload_bytes: number;
        max_upload_files: number;
        steps: { id: string; title: string; body: string }[];
      }>("/import/guide"),
    rescan: () => request<ImportResult>("/import/rescan", { method: "POST" }),
    scanPath: (path: string) =>
      request<ImportResult>("/import/scan-path", {
        method: "POST",
        body: JSON.stringify({ path }),
      }),
    upload: async (files: File[]): Promise<ImportResult> => {
      const form = new FormData();
      for (const f of files) form.append("files", f, f.name);
      const res = await fetch(`${BASE}/import/upload`, { method: "POST", body: form });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      return res.json();
    },
  },

  ccConfig: {
    overview: () => request<CcOverview>("/cc-config/overview"),
    skills: (scope?: CcScope) =>
      request<{ items: CcMdItem[] }>(`/cc-config/skills${scope ? `?scope=${scope}` : ""}`),
    agents: (scope?: CcScope) =>
      request<{ items: CcMdItem[] }>(`/cc-config/agents${scope ? `?scope=${scope}` : ""}`),
    commands: (scope?: CcScope) =>
      request<{ items: CcMdItem[] }>(`/cc-config/commands${scope ? `?scope=${scope}` : ""}`),
    outputStyles: (scope?: CcScope) =>
      request<{ items: CcMdItem[] }>(`/cc-config/output-styles${scope ? `?scope=${scope}` : ""}`),
    plugins: () => request<CcPluginsResponse>("/cc-config/plugins"),
    mcp: () => request<CcMcpResponse>("/cc-config/mcp"),
    hooks: () => request<{ items: CcHookSource[] }>("/cc-config/hooks"),
    settings: () => request<{ items: CcSettingsSource[] }>("/cc-config/settings"),
    memory: () => request<{ items: CcMemoryItem[] }>("/cc-config/memory"),
    file: (absPath: string) =>
      request<CcFileResponse>(`/cc-config/file?path=${encodeURIComponent(absPath)}`),
    write: (args: CcWriteArgs) =>
      request<CcMutationResult>("/cc-config/file", {
        method: "PUT",
        body: JSON.stringify(args),
      }),
    delete: (args: CcDeleteArgs) =>
      request<CcMutationResult>("/cc-config/file", {
        method: "DELETE",
        body: JSON.stringify(args),
      }),
    marketplaces: () => request<CcMarketplacesResponse>("/cc-config/marketplaces"),
    keybindings: () => request<CcKeybindings>("/cc-config/keybindings"),
    statusline: () => request<CcStatusline>("/cc-config/statusline"),
    hookScripts: () => request<CcHookScripts>("/cc-config/hook-scripts"),
    backups: (params?: { scope?: "user" | "project"; type?: CcArtifactType }) =>
      requestBackupsHelper(params),
  },

  run: {
    list: () => request<RunListResponse>("/run"),
    history: (limit = 50) =>
      request<{ items: DashboardRunHistoryItem[] }>(`/run/history?limit=${limit}`),
    binary: () => request<{ found: boolean; path: string | null }>("/run/binary"),
    cwds: () => request<{ items: CwdSuggestion[] }>("/run/cwds"),
    files: (cwd: string, q?: string) => {
      const qs = new URLSearchParams({ cwd });
      if (q) qs.set("q", q);
      return request<{ items: string[] }>(`/run/files?${qs.toString()}`);
    },
    start: (args: RunStartArgs) =>
      request<RunHandle>("/run", { method: "POST", body: JSON.stringify(args) }),
    get: (id: string, opts?: { envelopes?: boolean }) =>
      request<RunHandle>(`/run/${encodeURIComponent(id)}${opts?.envelopes ? "?envelopes=1" : ""}`),
    send: (id: string, text: string) =>
      request<{ messageId: string }>(`/run/${encodeURIComponent(id)}/message`, {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    kill: (id: string) =>
      request<{ ok: true }>(`/run/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  vpsStatus: {
    get: () => request<VpsStatusResponse>("/vps-status"),
  },

  inbox: {
    /** POST /api/inbox — session-authed quick capture from the dashboard UI (FAB). */
    capture: (text: string) =>
      request<{ ok: boolean; id: string }>("/inbox", {
        method: "POST",
        body: JSON.stringify({ text }),
      }),
    /**
     * GET /api/inbox?status=new|pending|classified|all — newest first, max 200.
     * The server may return either a bare array or { items: [...] }; normalize
     * to a plain array so the UI has one shape to deal with.
     */
    list: async (status: InboxListStatus = "all"): Promise<InboxItem[]> => {
      const res = await request<InboxItem[] | { items: InboxItem[] }>(
        `/inbox?status=${encodeURIComponent(status)}`
      );
      return Array.isArray(res) ? res : (res.items ?? []);
    },
    reclassify: (id: string) =>
      request<{ ok: boolean; item?: InboxItem }>(`/inbox/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "reclassify" }),
      }),
    dismiss: (id: string) =>
      request<{ ok: boolean; item?: InboxItem }>(`/inbox/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "dismiss" }),
      }),
    /** Manual correction — server marks the item as classified. */
    update: (id: string, fields: InboxUpdateFields) =>
      request<{ ok: boolean; item?: InboxItem }>(`/inbox/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(fields),
      }),
    /** Image served behind nginx basic auth — plain <img src> works. */
    imageUrl: (id: string) => `${BASE}/inbox/${encodeURIComponent(id)}/image`,
    /**
     * PATCH {action:"promote-to-task"} — creates a life-layer task from a
     * classified inbox item. Payload fields win, classified columns are the
     * server-side fallback.
     */
    promoteToTask: (id: string, fields: InboxPromoteFields = {}) =>
      request<{ ok: boolean; taskId: string }>(`/inbox/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "promote-to-task", ...fields }),
      }),
  },

  // --- Life layer (HERMES): Today / Tasks / Routines ---

  today: {
    /** GET /api/today — single payload for the Today screen (optional work/personal scope). */
    get: (scope?: "praca" | "prywatne") =>
      request<TodayPayload>(`/today${scope ? `?scope=${encodeURIComponent(scope)}` : ""}`),
  },

  tasks: {
    /** GET /api/tasks?status=open|done — open: due first then priority; done: last 15. */
    list: (status: LifeTaskListStatus = "open", scope?: "praca" | "prywatne") =>
      request<{ tasks: LifeTask[] }>(
        `/tasks?status=${encodeURIComponent(status)}${
          scope ? `&scope=${encodeURIComponent(scope)}` : ""
        }`
      ),
    create: (fields: NewLifeTaskFields) =>
      request<{ ok: boolean; task: LifeTask }>("/tasks", {
        method: "POST",
        body: JSON.stringify(fields),
      }),
    /** Completing a recurring task returns the spawned next occurrence in `nextTask`. */
    toggleDone: (id: string) =>
      request<{ ok: boolean; task: LifeTask; nextTask: LifeTask | null }>(
        `/tasks/${encodeURIComponent(id)}`,
        { method: "PATCH", body: JSON.stringify({ action: "toggle-done" }) }
      ),
    toggleTop3: (id: string) =>
      request<{ ok: boolean; task: LifeTask }>(`/tasks/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "toggle-top3" }),
      }),
    /** Field corrections — absent field keeps the value, explicit null clears it. */
    update: (id: string, fields: LifeTaskUpdateFields) =>
      request<{ ok: boolean; task: LifeTask }>(`/tasks/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(fields),
      }),
    delete: (id: string) =>
      request<{ ok: boolean }>(`/tasks/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  routines: {
    /** GET /api/routines — non-archived, enriched with checkedToday/streak/last14. */
    list: () => request<{ routines: LifeRoutineEnriched[] }>("/routines"),
    create: (fields: NewLifeRoutineFields) =>
      request<{ ok: boolean; routine: LifeRoutine }>("/routines", {
        method: "POST",
        body: JSON.stringify(fields),
      }),
    /** Check/uncheck a day ('YYYY-MM-DD', default today in Europe/Warsaw). */
    toggleCheck: (id: string, date?: string) =>
      request<{ ok: boolean; checked: boolean; date: string }>(
        `/routines/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(
            date ? { action: "toggle-check", date } : { action: "toggle-check" }
          ),
        }
      ),
    archive: (id: string) =>
      request<{ ok: boolean; routine: LifeRoutine }>(`/routines/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "archive" }),
      }),
  },

  briefing: {
    /**
     * GET /api/briefing — returns the latest command-center briefing, or null
     * when none has been ingested yet (the server replies 404 in that case,
     * which is an expected empty state rather than an error).
     */
    get: async (): Promise<Briefing | null> => {
      const res = await fetch(`${BASE}/briefing`, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 404) return null;
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
  },

  jarvisBriefing: {
    /**
     * GET /api/jarvis/briefing/today — latest JARVIS daily briefing (spec §4),
     * or null when none exists yet (server replies 404, an expected empty state
     * rather than an error — same normalization pattern as briefing.get).
     */
    getToday: async (): Promise<JarvisBriefing | null> => {
      const res = await fetch(`${BASE}/jarvis/briefing/today`, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 404) return null;
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      return res.json();
    },
  },

  // --- Trakcja (project & sprint tracking): GET /api/trakcja, PATCH /items/:id ---

  trakcja: {
    /** GET /api/trakcja — full board: projects (+ items) and the portfolio synthesis. */
    get: () => request<TrakcjaPayload>("/trakcja"),
    /** PATCH /api/trakcja/items/:id {action:"resolve"} — marks one project_item DONE. */
    resolveItem: (id: string) =>
      request<{ ok: boolean; item: TrakcjaItem }>(`/trakcja/items/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ action: "resolve" }),
      }),
  },

  auth: {
    /**
     * GET /api/auth/me — { authEnabled, user }. A 401 (login enabled but no
     * session) is an expected "not signed in" state, normalized to user:null
     * rather than thrown — same pattern as briefing.get's 404 handling.
     */
    me: async (): Promise<AuthMe> => {
      const res = await fetch(`${BASE}/auth/me`, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 401) return { authEnabled: true, user: null };
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    logout: () =>
      request<{ ok: boolean }>("/auth/logout", { method: "POST", body: JSON.stringify({}) }),
  },

  ecosystem: {
    /** GET /api/ecosystem — grouped app/project entities + live status badge. */
    get: () => request<EcosystemPayload>("/ecosystem"),
    /** GET /api/ecosystem/:id — one entity: registry + status + Trakcja profile + memory refs. */
    getOne: (id: string) => request<EcosystemDetail>(`/ecosystem/${encodeURIComponent(id)}`),
  },

  commands: {
    /** GET /api/commands — grouped slash-command catalog + drift flags. */
    list: () => request<CommandsPayload>("/commands"),
    /** GET /api/commands/:id — one command: curated entry (goal/process/connections). */
    getOne: (id: string) => request<CommandDetail>(`/commands/${encodeURIComponent(id)}`),
  },
};

// --- Command-center briefing (GET /api/briefing) ---

export type BriefingBadgeTone = "ok" | "warn" | "danger";

export interface BriefingClient {
  id: string;
  name: string;
  badge: string;
  badgeTone: BriefingBadgeTone;
  description: string;
  progressPct: number | null;
  lastContact: string | null;
  nextStep: string;
  overdue: string | null;
}

export interface BriefingDecision {
  title: string;
  context: string;
  since: string | null;
}

export interface BriefingAgentWork {
  status: "done" | "in-progress" | "error";
  text: string;
}

export interface Briefing {
  generatedAt: string;
  headline: string;
  clients: BriefingClient[];
  decisions: BriefingDecision[];
  agentWork: BriefingAgentWork[];
  staleness: string | null;
}

// --- JARVIS daily briefing (GET /api/jarvis/briefing/today) — spec §4 ---

export interface JarvisEmailDraft {
  id: string;
  to: string;
  subjectDraft: string;
  bodyDraft: string;
  sourceThreadId: string;
  why: string;
}

export interface JarvisNextMove {
  id: string;
  text: string;
  project: string;
  why: string;
}

export interface JarvisIdeaPeek {
  id: string;
  snippet: string;
}

export interface JarvisBriefing {
  date: string;
  generatedAt: string;
  status: "ready" | "partial" | "failed";
  tiles: {
    emailDrafts: JarvisEmailDraft[];
    nextMoves: JarvisNextMove[];
    ideasInbox: { count: number; latest: JarvisIdeaPeek[] };
  };
  meta: Record<string, unknown> | null;
}

// --- VPS infrastructure monitoring (GET /api/vps-status) ---

export interface VpsTargetStatus {
  id: string;
  name: string;
  url: string;
  expect: number[];
  status: "up" | "down" | "unknown";
  httpCode: number | null;
  latencyMs: number | null;
  lastChange: string | null;
  lastChecked: string | null;
}

export interface VpsStatusResponse {
  targets: VpsTargetStatus[];
}

// --- Quick-capture inbox (GET/PATCH /api/inbox) ---

export type InboxListStatus = "new" | "pending" | "classified" | "all";
export type InboxItemStatus = "new" | "pending" | "classified" | "dismissed";
export type InboxItemType =
  | "task"
  | "event"
  | "journal"
  | "idea"
  | "note"
  // kolejka operacyjna (2026-08-26) — musi odpowiadac ALLOWED_TYPES na serwerze
  | "alert"
  | "mail"
  | "blocker"
  | "heartbeat";

export const INBOX_TYPES: InboxItemType[] = [
  "task",
  "event",
  "journal",
  "idea",
  "note",
  // dolozone przez kolejke operacyjna — musi byc zgodne z ALLOWED_TYPES na serwerze
  "alert",
  "mail",
  "blocker",
  "heartbeat",
];
export const INBOX_DOMAINS = [
  "rodzina",
  "ja",
  "zdrowie",
  "finanse",
  "syhi-klienci",
  "vantage-labs",
  "poboczne-apki",
  "yt",
  "cybernetyka",
  "skarbiec",
] as const;

export interface InboxItem {
  id: string;
  created_at: string;
  source: string | null;
  hint: string | null;
  raw_text: string | null;
  image_path: string | null;
  status: InboxItemStatus;
  type: InboxItemType | null;
  domain: string | null;
  title: string | null;
  due_at: string | null;
  remind_at: string | null;
  details: string | null;
  classified_at: string | null;
  error: string | null;
}

/** Manual correction payload — camelCase per the capture contract. */
export interface InboxUpdateFields {
  type?: InboxItemType;
  domain?: string;
  title?: string;
  dueAt?: string | null;
  remindAt?: string | null;
  details?: string | null;
}

/** promote-to-task overrides — absent field falls back to the classified column. */
export interface InboxPromoteFields {
  title?: string;
  domain?: string | null;
  dueAt?: string | null;
  remindAt?: string | null;
}

// --- Life layer (HERMES): /api/today, /api/tasks, /api/routines ---

export type LifeTaskListStatus = "open" | "done";
export type LifeTaskPriority = "LOW" | "MEDIUM" | "HIGH";
export type LifeTaskRecurrence = "daily" | "weekly" | "monthly";
export type LifeRoutineTimeOfDay = "MORNING" | "AFTERNOON" | "EVENING" | "ANYTIME";
export type LifeRoutineMode = "ONGOING" | "FIXED";

export const LIFE_TASK_PRIORITIES: LifeTaskPriority[] = ["LOW", "MEDIUM", "HIGH"];
export const LIFE_TASK_RECURRENCES: LifeTaskRecurrence[] = ["daily", "weekly", "monthly"];
export const LIFE_TIMES_OF_DAY: LifeRoutineTimeOfDay[] = [
  "MORNING",
  "AFTERNOON",
  "EVENING",
  "ANYTIME",
];

/** Row from the `domains` table (seeded server-side, user-editable). */
export interface LifeDomain {
  id: string;
  name: string;
  color: string;
  icon: string | null;
  sort_order: number;
}

/**
 * Static mirror of the server-side domain seed (server/db.js LIFE_DOMAINS) —
 * used by forms on pages that don't receive `domains` in their payload (the
 * Today payload carries the live table; same precedent as INBOX_DOMAINS).
 */
export const LIFE_DOMAINS: LifeDomain[] = [
  { id: "dom", name: "Dom", color: "#0EA5E9", icon: "home", sort_order: 0 },
  { id: "rodzina", name: "Rodzina", color: "#EC4899", icon: "heart", sort_order: 1 },
  { id: "ja", name: "Ja", color: "#8B5CF6", icon: "user", sort_order: 2 },
  { id: "zdrowie", name: "Zdrowie", color: "#22C55E", icon: "activity", sort_order: 3 },
  { id: "finanse", name: "Finanse", color: "#10B981", icon: "wallet", sort_order: 4 },
  { id: "syhi-klienci", name: "SYHI Klienci", color: "#6366F1", icon: "users", sort_order: 5 },
  { id: "vantage-labs", name: "Vantage Labs", color: "#047857", icon: "briefcase", sort_order: 6 },
  { id: "poboczne-apki", name: "Poboczne apki", color: "#F59E0B", icon: "rocket", sort_order: 7 },
  { id: "yt", name: "YouTube", color: "#EF4444", icon: "video", sort_order: 8 },
  { id: "cybernetyka", name: "Cybernetyka", color: "#0284C7", icon: "cpu", sort_order: 9 },
  { id: "skarbiec", name: "Skarbiec", color: "#A16207", icon: "archive", sort_order: 10 },
];

/** Row from the `tasks` table — snake_case as returned by SELECT *. */
export interface LifeTask {
  id: string;
  title: string;
  notes: string | null;
  domain: string | null;
  priority: LifeTaskPriority;
  status: "OPEN" | "DONE";
  due_at: string | null;
  is_top3: number; // 0 | 1 in SQLite
  top3_date: string | null;
  recurrence: LifeTaskRecurrence | null;
  recurrence_parent: string | null;
  last_activity_at: string;
  completed_at: string | null;
  created_at: string;
}

/** Row from the `routines` table. */
export interface LifeRoutine {
  id: string;
  name: string;
  description: string | null;
  time_of_day: LifeRoutineTimeOfDay;
  specific_time: string | null; // "HH:MM"
  notify: number; // 0 | 1
  mode: LifeRoutineMode;
  days_count: number | null;
  start_date: string | null; // "YYYY-MM-DD"
  archived: number; // 0 | 1
  sort_order: number;
  domain: string | null;
  created_at: string;
}

/** Routine enriched by GET /api/routines and /api/today. */
export interface LifeRoutineEnriched extends LifeRoutine {
  checkedToday: boolean;
  /** Consecutive days, counted from today-or-yesterday backwards. */
  streak: number;
  /** 14 booleans for the heatmap, oldest day first. */
  last14: boolean[];
}

export interface TodayPayload {
  top3: LifeTask[];
  slipping: LifeTask[];
  openTasks: LifeTask[];
  routines: LifeRoutineEnriched[];
  domains: LifeDomain[];
}

export interface NewLifeTaskFields {
  title: string;
  notes?: string | null;
  domain?: string | null;
  priority?: LifeTaskPriority;
  dueAt?: string | null;
  recurrence?: LifeTaskRecurrence | null;
  remindAt?: string | null;
}

/** Same "absent = keep, null = clear" contract as InboxUpdateFields. */
export interface LifeTaskUpdateFields {
  title?: string;
  domain?: string | null;
  priority?: LifeTaskPriority;
  dueAt?: string | null;
}

export interface NewLifeRoutineFields {
  name: string;
  description?: string | null;
  timeOfDay?: LifeRoutineTimeOfDay;
  specificTime?: string | null;
  notify?: boolean;
  mode?: LifeRoutineMode;
  daysCount?: number | null;
  startDate?: string;
  sortOrder?: number;
  domain?: string | null;
}

function requestBackupsHelper(params?: { scope?: "user" | "project"; type?: CcArtifactType }) {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set("scope", params.scope);
  if (params?.type) qs.set("type", params.type);
  const q = qs.toString();
  return request<{ items: CcBackup[] }>(`/cc-config/backups${q ? `?${q}` : ""}`);
}

export type CcArtifactType = "skills" | "agents" | "commands" | "output-styles" | "memory";

export interface CcWriteArgs {
  scope: "user" | "project";
  type: CcArtifactType;
  name?: string;
  content: string;
}

export interface CcDeleteArgs {
  scope: "user" | "project";
  type: CcArtifactType;
  name?: string;
}

export interface CcMutationResult {
  ok: true;
  file: string;
  target: string;
  backupPath: string | null;
  created?: boolean;
}

export interface CcBackup {
  scope: "user" | "project";
  type: CcArtifactType;
  name: string;
  backupPath: string;
  isDir: boolean;
  mtime: number;
  size: number | null;
}

export type CcScope = "user" | "project" | "all";

export interface CcMdItem {
  scope: "user" | "project";
  name: string;
  file?: string;
  path?: string;
  size: number;
  mtime: number;
  truncated: boolean;
  frontmatter: Record<string, string>;
  preview: string;
}

export interface CcPluginContributions {
  skills: number;
  agents: number;
  commands: number;
  outputStyles: number;
  hooks: number;
  pluginJson: {
    name?: string;
    description?: string;
    version?: string;
    author?: { name?: string; email?: string };
    homepage?: string;
    repository?: string;
    license?: string;
    keywords?: string[];
  } | null;
}

export interface CcPlugin {
  key: string;
  name: string;
  marketplace: string | null;
  scope: string;
  version: string | null;
  installPath: string | null;
  installedAt: string | null;
  lastUpdated: string | null;
  gitCommitSha: string | null;
  installPathExists: boolean;
  enabled: boolean | null;
  contributes: CcPluginContributions | null;
}

export interface CcPluginsResponse {
  manifestPath: string;
  manifestExists: boolean;
  plugins: CcPlugin[];
}

export interface CcMcpServer {
  name: string;
  source: string;
  kind: "stdio" | "http" | "unknown";
  command?: string;
  args?: string[];
  envNames?: string[];
  url?: string;
  headers?: string[];
}

export interface CcMcpResponse {
  user: CcMcpServer[];
  projectScoped: CcMcpServer[];
}

export interface CcHookEntry {
  matcher: string;
  type: string;
  command: string | null;
  timeout: number | null;
}

export interface CcHookSource {
  scope: "user" | "project" | "project-local";
  file: string;
  exists: boolean;
  hooks: Record<string, CcHookEntry[]>;
}

export interface CcSettingsSource {
  scope: "user" | "project" | "project-local";
  file: string;
  exists: boolean;
  data?: unknown;
  raw_size?: number;
}

export interface CcMemoryItem {
  scope: "user" | "project";
  file: string;
  size: number;
  mtime: number;
  truncated: boolean;
  preview: string;
}

export interface CcFileResponse {
  ok: true;
  file: string;
  text: string;
  size: number;
  mtime: number;
  truncated: boolean;
}

export interface CcOverview {
  roots: {
    claudeHome: string;
    projectClaudeDir: string;
    projectRoot: string;
    claudeJson: string;
  };
  counts: {
    skills: { user: number; project: number };
    agents: { user: number; project: number };
    commands: { user: number; project: number };
    outputStyles: { user: number; project: number };
    plugins: number;
    pluginsEnabled: number;
    pluginsDisabled: number;
    marketplaces: number;
    keybindings: number;
    mcpServers: { user: number; project: number };
    hooks: Record<string, number>;
    memory: number;
    settingsFiles: number;
  };
}

export interface CcMarketplace {
  name: string;
  source: { source?: string; repo?: string; url?: string } | null;
  installLocation: string | null;
  lastUpdated: string | null;
  pluginCount: number | null;
  marketplaceName: string | null;
  marketplaceDescription: string | null;
  marketplaceOwner: { name?: string; url?: string } | null;
}

export interface CcMarketplacesResponse {
  knownPath: string;
  knownExists: boolean;
  items: CcMarketplace[];
}

export interface CcKeybindingGroup {
  context: string;
  bindings: { key: string; action: string }[];
}

export interface CcKeybindings {
  file: string;
  exists: boolean;
  schema?: string | null;
  docs?: string | null;
  groups: CcKeybindingGroup[];
}

export interface CcStatuslineScript {
  file: string;
  size: number;
  mtime: number;
  truncated: boolean;
  preview: string;
}

export interface CcStatusline {
  config: { type?: string; command?: string } | null;
  scripts: CcStatuslineScript[];
}

export interface CcHookScripts {
  dir: string;
  items: { name: string; file: string; size: number; mtime: number }[];
}

export type RunMode = "headless" | "conversation";
export type RunStatus = "spawning" | "running" | "completed" | "error" | "killed" | "abandoned";
export type PermissionMode = "acceptEdits" | "default" | "plan" | "bypassPermissions";
export type EffortLevel = "" | "low" | "medium" | "high" | "xhigh" | "max";

export interface RunStartArgs {
  prompt: string;
  mode: RunMode;
  cwd?: string;
  model?: string;
  permissionMode?: PermissionMode;
  resumeSessionId?: string;
  effort?: EffortLevel;
}

export interface RunHandle {
  id: string;
  pid: number | null;
  mode: RunMode;
  cwd: string;
  model: string | null;
  permissionMode: PermissionMode;
  effort: EffortLevel | null;
  prompt: string;
  argv: string[];
  resumeSessionId: string | null;
  status: RunStatus;
  startedAt: number;
  endedAt: number | null;
  exitCode: number | null;
  signal: string | null;
  error: string | null;
  sessionId: string | null;
  envelopeCount: number;
  stdoutTail: string;
  stderrTail: string;
  envelopes?: unknown[]; // present when fetched with ?envelopes=1
}

export interface RunListResponse {
  items: RunHandle[];
  maxConcurrent: number;
  activeCount: number;
}

/**
 * A row from the persistent `dashboard_runs` sqlite table — every run ever
 * spawned via /api/run, including completed / errored / killed ones long
 * after the in-memory handle has been reaped.
 */
export interface DashboardRunHistoryItem {
  id: string;
  session_id: string | null;
  mode: RunMode;
  cwd: string;
  model: string | null;
  permission_mode: PermissionMode | null;
  effort: EffortLevel | null;
  resume_session_id: string | null;
  prompt_preview: string | null;
  status: RunStatus;
  exit_code: number | null;
  started_at: string;
  ended_at: string | null;
  isLive: boolean;
}

export interface CwdSuggestion {
  kind: "dashboard" | "home" | "recent";
  path: string;
  label: string;
}

export interface ModelChoice {
  id: string; // value sent to claude --model
  label: string; // user-facing
  hint?: string;
}

// Effort level choices for `claude --effort`. Higher = more thinking tokens
// before the assistant turn. Empty inherits the model's default.
export interface EffortChoice {
  id: EffortLevel;
  label: string;
  hint?: string;
}

export const RUN_EFFORT_CHOICES: EffortChoice[] = [
  { id: "", label: "Default (model decides)", hint: "No --effort flag" },
  { id: "low", label: "Low", hint: "Fast, minimal thinking" },
  { id: "medium", label: "Medium", hint: "Balanced" },
  { id: "high", label: "High", hint: "More reasoning, slower" },
  { id: "xhigh", label: "Extra-high", hint: "Deep reasoning" },
  { id: "max", label: "Max", hint: "All-out — slowest, most tokens" },
];

// Curated model list. "" means "inherit from settings.json" — no --model flag.
export const RUN_MODEL_CHOICES: ModelChoice[] = [
  { id: "", label: "Inherit from settings", hint: "Use whatever your settings.json model is" },
  {
    id: "claude-opus-4-8[1m]",
    label: "Opus 4.8 (1M context)",
    hint: "Highest capability, 1M token window",
  },
  {
    id: "claude-opus-4-7[1m]",
    label: "Opus 4.7 (1M context)",
    hint: "Previous Opus, 1M token window",
  },
  { id: "sonnet", label: "Sonnet 4.6", hint: "Balanced capability and speed" },
  { id: "haiku", label: "Haiku 4.5", hint: "Fastest, lightest" },
];

export interface ImportResult {
  ok: boolean;
  source: "default" | "path" | "upload";
  path?: string;
  imported: number;
  skipped: number;
  backfilled?: number;
  errors: number;
  sessions_seen?: number;
  files_scanned?: number;
  files_received?: number;
  entries_extracted?: number;
  entries_skipped?: number;
}

// --- Trakcja (project & sprint tracking): GET /api/trakcja ---

export type TrakcjaType = "CLIENT" | "INTERNAL";
export type TrakcjaMomentum = "GREEN" | "YELLOW" | "RED";
export type TrakcjaItemKind = "GOAL" | "ASSUMPTION" | "WIN" | "LOSS" | "BLOCKER";
export type TrakcjaItemStatus = "OPEN" | "DONE" | "VALIDATED" | "INVALIDATED" | "BLOCKED";

/** Row from project_items (snake_case as returned by SELECT *). */
export interface TrakcjaItem {
  id: string;
  project_id: string;
  kind: TrakcjaItemKind;
  text: string;
  status: TrakcjaItemStatus;
  evidence: string | null;
  created_at: string;
}

/** Row from projects + its items; data_gaps/sources_used parsed from JSON by the router. */
export interface TrakcjaProject {
  id: string;
  name: string;
  type: TrakcjaType;
  owner: string | null;
  stage: string | null;
  current_sprint: string | null;
  current_goal: string | null;
  next_step: string | null;
  momentum: TrakcjaMomentum;
  momentum_reason: string | null;
  last_activity_at: string | null;
  last_activity_type: string | null;
  deadline_at: string | null;
  money_note: string | null;
  source_freshness: string | null;
  data_gaps: string[];
  sources_used: string[];
  created_at: string;
  items: TrakcjaItem[];
}

export interface TrakcjaPortfolioDecision {
  project: string;
  what: string;
}

export interface TrakcjaPortfolioTraction {
  id: string;
  type: string;
  momentum: string;
  one_liner: string;
}

export interface TrakcjaPortfolio {
  generatedAt?: string;
  headline: string;
  by_traction: TrakcjaPortfolioTraction[];
  losing_traction: string[];
  needs_decision: TrakcjaPortfolioDecision[];
  note: string;
}

export interface TrakcjaPayload {
  generatedAt: string | null;
  projects: TrakcjaProject[];
  portfolio: TrakcjaPortfolio | null;
}

// --- App-level OAuth login (GET /api/auth/me) ---

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  photo: string | null;
}

export interface AuthMe {
  authEnabled: boolean;
  user: AuthUser | null;
}

// --- Ekosystem / Aplikacje (GET /api/ecosystem) — unified App/Project entity ---

export type EcoStatus = "live" | "stopped" | "unknown";

export interface EcoVps {
  vhost?: string;
  container?: string;
  pm2?: string;
}

export interface EcoItem {
  id: string;
  name: string;
  kind: "APP" | "PROJECT" | "BOTH";
  group: string;
  vps: EcoVps | null;
  hasProfile: boolean;
  memoryCount: number;
  status: EcoStatus;
  statusDetail: string | null;
}

export interface EcoGroup {
  key: string;
  label: string;
  color: string;
  items: EcoItem[];
}

export interface EcosystemPayload {
  generatedAt: string | null;
  groups: EcoGroup[];
}

export interface EcosystemDetail {
  id: string;
  name: string;
  kind: "APP" | "PROJECT" | "BOTH";
  group: string;
  vps: EcoVps | null;
  trakcjaId?: string;
  memoryFiles?: string[];
  status: EcoStatus;
  statusDetail: string | null;
  profile: TrakcjaProject | null;
  profileMissing: boolean;
  threads: EcoThread[];
  threadsGeneratedAt: string | null;
}

export interface EcoThread {
  sessionId: string;
  date: string | null;
  title: string;
  summary: string;
  edits: number;
  confidence: "high" | "med" | "low";
}

// --- Komendy (GET /api/commands) — curated slash-command catalog ---

export interface CmdItem {
  id: string;
  slash: string;
  name: string;
  group: string;
  icon: string | null;
  goal: string;
  connections: number;
  present: boolean | null;
}

export interface CmdGroup {
  key: string;
  label: string;
  color: string;
  items: CmdItem[];
}

export interface CmdUndocumented {
  id: string;
  slash: string;
  source: string | null;
}

export interface CommandsPayload {
  generatedAt: string | null;
  groups: CmdGroup[];
  undocumented: CmdUndocumented[];
}

export interface CmdProcessStep {
  step: number;
  label: string;
  desc: string;
}

export interface CmdConnections {
  skills: { id: string; label: string }[];
  related: { id: string; relation: string }[];
  knowledge: { path: string; label: string }[];
}

export interface CommandDetail {
  id: string;
  slash: string;
  name: string;
  group: string;
  icon: string | null;
  goal: string;
  analogy?: string;
  whenUse: string[];
  whenNot: string[];
  process: CmdProcessStep[];
  connections: CmdConnections;
  match?: { keywords: string[] };
  present: boolean | null;
  discoveredAt: string | null;
}
